import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react'
import { UserRole, UserRoles } from '../constants/userRoles'
import { detectAccountMode } from '../services/auth'
import { BaseUser, OrganizationContext } from '../types/userTypes'
import { useSession } from './SessionContext'
import { logger } from '../services/logger'

interface ProModeContextType {
  isProMode: boolean
  isLoading: boolean
  organizationContext: OrganizationContext | null

  // Pro Mode specific functions
  canActAsCorporate: boolean
  effectiveRole: UserRole // The role the user effectively has (Admin becomes Corporate in Pro Mode)
  proModeAdmin: BaseUser | null

  // Mode switching functions
  refreshModeDetection: () => Promise<void>

  // Permission helpers for Pro Mode
  hasProModePrivilege: (action: string) => boolean
}

const ProModeContext = createContext<ProModeContextType | undefined>(undefined)

// eslint-disable-next-line react-refresh/only-export-components
export const useProMode = () => {
  const context = useContext(ProModeContext)
  if (context === undefined) {
    throw new Error('useProMode must be used within a ProModeProvider')
  }
  return context
}

interface ProModeProviderProps {
  children: ReactNode
}

export const ProModeProvider: React.FC<ProModeProviderProps> = ({
  children,
}) => {
  const { currentUser, userRole, loading: authLoading } = useSession()
  const [isProMode, setIsProMode] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [organizationContext, setOrganizationContext] =
    useState<OrganizationContext | null>(null)

  // Detect if current user can act as Corporate in Pro Mode
  const canActAsCorporate = React.useMemo(() => {
    if (!userRole) return false

    // Super Admin (platform level - top of hierarchy) and Admin (organization level)
    // can act as Corporate in Pro Mode
    // Super Admin has all permissions by default
    // Admin with admin privileges acts as Corporate for their organization
    // Note: hasAdminPrivileges needs to be fetched from user document for Admin
    return (
      isProMode &&
      (userRole === UserRoles.SUPER_ADMIN || userRole === UserRoles.ADMIN)
      // TODO: Add hasAdminPrivileges check when we have access to full user document
    )
  }, [isProMode, userRole])

  // Determine effective role for permissions
  const effectiveRole = React.useMemo((): UserRole => {
    if (!userRole) return UserRoles.TENANT

    if (canActAsCorporate) {
      return UserRoles.CORPORATE // Admin acts as Corporate in Pro Mode
    }

    return userRole || UserRoles.TENANT
  }, [userRole, canActAsCorporate])

  // Get the Pro Mode admin user (the admin acting as corporate)
  const proModeAdmin = React.useMemo(() => {
    if (canActAsCorporate && currentUser) {
      // TODO: Return full BaseUser object when we have access to user document
      return {
        uid: currentUser.uid,
        email: currentUser.email || '',
        role: userRole,
        displayName: currentUser.displayName || '',
        // Add other BaseUser fields as needed
      } as BaseUser
    }
    return null
  }, [canActAsCorporate, currentUser, userRole])

  // Detect account mode on auth changes
  const detectMode = useCallback(async () => {
    if (import.meta.env.VITE_ENABLE_CONSOLE_LOGGING === 'true') {
      logger.debug(
        '🔍 ProModeContext detectMode called - userRole:',
        { userRole, authLoading, hasCurrentUser: !!currentUser },
        'auth'
      )
    }

    if (!currentUser || authLoading) {
      if (import.meta.env.VITE_ENABLE_CONSOLE_LOGGING === 'true') {
        logger.debug('⏭️ ProModeContext early return - not ready', null, 'auth')
      }
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)
      if (import.meta.env.VITE_ENABLE_CONSOLE_LOGGING === 'true') {
        logger.debug(
          '🌐 ProModeContext calling detectAccountMode...',
          null,
          'auth'
        )
      }
      const context = await detectAccountMode()
      if (import.meta.env.VITE_ENABLE_CONSOLE_LOGGING === 'true') {
        logger.debug('📊 ProModeContext received context:', context, 'auth')
      }

      setOrganizationContext(context)

      // Check for SUPER_ADMIN mode override from sessionStorage
      const modeOverride = sessionStorage.getItem('modeOverride')

      // SUPER_ADMIN users can toggle between modes via mode selector
      if (import.meta.env.VITE_ENABLE_CONSOLE_LOGGING === 'true') {
        logger.debug(
          '🎭 ProModeContext checking userRole:',
          { userRole, superAdmin: UserRoles.SUPER_ADMIN, modeOverride },
          'auth'
        )
      }
      if (userRole === UserRoles.SUPER_ADMIN) {
        if (modeOverride) {
          // SUPER_ADMIN has explicitly chosen a mode
          if (import.meta.env.VITE_ENABLE_CONSOLE_LOGGING === 'true') {
            logger.debug(
              '👑 SUPER_ADMIN with mode override:',
              { modeOverride },
              'auth'
            )
          }
          setIsProMode(modeOverride === 'simple' || modeOverride === 'pro')
        } else {
          // Default SUPER_ADMIN to Pro Mode (as per user requirement)
          if (import.meta.env.VITE_ENABLE_CONSOLE_LOGGING === 'true') {
            logger.debug(
              '👑 SUPER_ADMIN detected - DEFAULTING to Pro Mode',
              null,
              'auth'
            )
          }
          setIsProMode(true) // Default to Pro Mode for SUPER_ADMIN
        }
      } else {
        if (import.meta.env.VITE_ENABLE_CONSOLE_LOGGING === 'true') {
          logger.debug(
            '👤 Regular user - using account mode:',
            { accountMode: context.accountMode },
            'auth'
          )
        }
        // isProMode is a FEATURE-paywall signal (has a Pro subscription), NOT a
        // gate on the dashboard itself — the dashboard is the default view for
        // everyone incl. free tier (see Dashboard.tsx). Respect the account mode
        // from subscription/plan; support 'simple' (legacy) + 'pro'. Currently
        // derived from the GLOBAL accountMetadata doc — a real per-account
        // subscription signal is future work when features get paywalled.
        setIsProMode(
          context.accountMode === 'simple' || context.accountMode === 'pro'
        )
      }

      // Log mode detection for debugging (only in debug mode)
      if (new URLSearchParams(window.location.search).get('debug')) {
        logger.debug('Account Mode Detection:', {
          mode: context.accountMode,
          corporateUsers: context.corporateUsers.length,
          totalUsers: context.totalUsers,
          userRole: userRole,
          canActAsCorporate: userRole === UserRoles.ADMIN, // TODO: Add hasAdminPrivileges check
        })
      }
    } catch (error) {
      console.error('❌ ProModeContext error detecting account mode:', error)
      logger.warn(
        '⚠️ ProModeContext error handling - userRole:',
        {
          userRole,
          error: error instanceof Error ? error.message : String(error),
        },
        'auth'
      )

      // SUPER_ADMIN defaults to Pro Mode (even on error)
      if (userRole === UserRoles.SUPER_ADMIN) {
        const modeOverride = sessionStorage.getItem('modeOverride')
        logger.debug(
          '👑 ERROR HANDLING: SUPER_ADMIN - using override or defaulting to Pro Mode',
          { modeOverride },
          'auth'
        )
        setIsProMode(
          modeOverride === 'simple' || modeOverride === 'pro' || !modeOverride
        )
      } else {
        logger.debug(
          '📱 ERROR HANDLING: Regular user - defaulting to Pro Mode',
          null,
          'auth'
        )
        // Default to Pro Mode on error for non-super-admin users
        setIsProMode(true)
      }
      setOrganizationContext({
        organizationId: 'default',
        accountMode: 'simple',
        planTier: 'basic',
        billingEnabled: false,
      })
    } finally {
      setIsLoading(false)
    }
  }, [currentUser, authLoading, userRole])

  // Refresh mode detection manually
  const refreshModeDetection = React.useCallback(async () => {
    await detectMode()
  }, [detectMode])

  // Run detection when auth state changes
  useEffect(() => {
    if (import.meta.env.VITE_ENABLE_CONSOLE_LOGGING === 'true') {
      logger.debug(
        '🔄 ProModeContext useEffect triggered - running detectMode',
        null,
        'auth'
      )
    }
    detectMode()
  }, [detectMode])

  // Pro Mode privilege checking
  const hasProModePrivilege = React.useCallback(
    (action: string): boolean => {
      if (!userRole) return false

      // Super Admin always has privileges
      if (userRole === UserRoles.SUPER_ADMIN) return true

      // In Pro Mode, Admin with privileges acts as Corporate
      if (isProMode && canActAsCorporate) {
        return hasProModeCorporatePrivileges(action)
      }

      // Standard role-based privileges
      return hasStandardPrivileges(userRole || UserRoles.TENANT, action)
    },
    [userRole, isProMode, canActAsCorporate]
  )

  const value: ProModeContextType = {
    isProMode,
    isLoading: isLoading || authLoading,
    organizationContext,
    canActAsCorporate,
    effectiveRole,
    proModeAdmin,
    refreshModeDetection,
    hasProModePrivilege,
  }

  return (
    <ProModeContext.Provider value={value}>{children}</ProModeContext.Provider>
  )
}

// Helper function to check Pro Mode Corporate privileges
function hasProModeCorporatePrivileges(action: string): boolean {
  const corporateActions = [
    'manage_billing',
    'create_admin_users',
    'create_manager_users',
    'create_tenant_users',
    'create_properties',
    'assign_properties',
    'view_corporate_users',
    'manage_organization',
    'send_invitations',
    'manage_user_roles',
  ]

  return corporateActions.includes(action)
}

// Helper function to check standard role privileges
function hasStandardPrivileges(role: UserRole, action: string): boolean {
  const rolePrivileges: Record<UserRole, string[]> = {
    [UserRoles.SUPER_ADMIN]: ['*'], // All privileges
    [UserRoles.CORPORATE]: [
      'manage_billing',
      'create_admin_users',
      'create_manager_users',
      'create_tenant_users',
      'create_properties',
      'assign_properties',
      'view_corporate_users',
      'manage_organization',
      'send_invitations',
      'manage_user_roles',
    ],
    [UserRoles.ADMIN]: [
      'create_manager_users',
      'create_tenant_users',
      'create_properties',
      'assign_properties',
      'view_admin_users',
      'send_invitations',
    ],
    [UserRoles.MANAGEMENT]: [
      'create_tenant_users',
      'assign_properties',
      'view_assigned_users',
      'send_tenant_invitations',
    ],
    [UserRoles.TENANT]: ['view_own_profile', 'update_own_profile'],
  }

  const privileges = rolePrivileges[role] || []
  return privileges.includes('*') || privileges.includes(action)
}

// Export the context for advanced usage
export { ProModeContext }

// Hook for Pro Mode specific operations
// eslint-disable-next-line react-refresh/only-export-components
export const useProModeOperations = () => {
  const { isProMode, canActAsCorporate, effectiveRole, hasProModePrivilege } =
    useProMode()
  const { currentUser } = useSession()

  // Check if user can perform Corporate-level actions in Pro Mode
  const canPerformCorporateAction = React.useCallback(
    (action: string): boolean => {
      if (!isProMode) return false
      return canActAsCorporate && hasProModePrivilege(action)
    },
    [isProMode, canActAsCorporate, hasProModePrivilege]
  )

  // Get appropriate dashboard route based on mode and role
  const getDashboardRoute = React.useCallback((): string => {
    if (effectiveRole === UserRoles.SUPER_ADMIN) return '/admin/dashboard'
    if (effectiveRole === UserRoles.CORPORATE) return '/corporate/dashboard'
    if (effectiveRole === UserRoles.ADMIN) return '/admin/dashboard'
    if (effectiveRole === UserRoles.MANAGEMENT) return '/manager/dashboard'
    return '/tenant/dashboard'
  }, [effectiveRole])

  // Get navigation items based on Pro Mode and effective role
  const getNavigationItems = React.useCallback(() => {
    const baseItems = [
      { label: 'Dashboard', path: getDashboardRoute(), icon: 'dashboard' },
    ]

    if (canPerformCorporateAction('manage_billing')) {
      baseItems.push({ label: 'Billing', path: '/billing', icon: 'billing' })
    }

    if (canPerformCorporateAction('create_admin_users')) {
      baseItems.push({
        label: 'Team Management',
        path: '/admin/users',
        icon: 'users',
      })
    }

    if (canPerformCorporateAction('create_properties')) {
      baseItems.push({
        label: 'Properties',
        path: '/properties',
        icon: 'properties',
      })
    }

    if (currentUser?.uid) {
      baseItems.push({
        label: 'Profile',
        path: `/team/${currentUser.uid}`,
        icon: 'profile',
      })
    }

    return baseItems
  }, [canPerformCorporateAction, getDashboardRoute, currentUser])

  return {
    canPerformCorporateAction,
    getDashboardRoute,
    getNavigationItems,
    isProMode,
    effectiveRole,
  }
}
